"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef } from "react";
import { UserCircleIcon } from "@heroicons/react/24/outline";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, type DragEndEvent, useSensor, useSensors } from "@dnd-kit/core";
import { horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TabToggle } from "@/components/common/TabToggle";
import { useSearchParams } from "next/navigation";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { useWaveCurationReorderMutation } from "@/hooks/waves/useWaveCurationReorderMutation";
import { getProfileWaveIdentity, useProfileWave } from "@/hooks/useProfileWave";
import { useWave } from "@/hooks/useWave";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { Time } from "@/helpers/time";
import { useAuth } from "@/components/auth/Auth";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useContentTab, WaveVotingState, type SetActiveContentTab } from "../ContentTabContext";
import MyStreamActionTooltip from "./MyStreamActionTooltip";
import MyStreamWaveCreateCurationAction from "./tabs/MyStreamWaveCreateCurationAction";
import MyStreamWaveCurationTabMenu from "./tabs/MyStreamWaveCurationTabMenu";
interface MyStreamWaveDesktopTabsProps {
  readonly activeTab: MyStreamWaveTab;
  readonly wave: ApiWave;
  readonly setActiveTab: SetActiveContentTab;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
  readonly showCreateCurationAction?: boolean | undefined;
}
interface TabOption {
  readonly key: string;
  readonly label: string;
  readonly panelId: string;
  readonly leadingIcon?: React.ReactNode | undefined;
  readonly leadingIconTooltipId?: string | undefined;
  readonly hasIndicator?: boolean | undefined;
  readonly action?: React.ReactNode | undefined;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const getCurationPanelId = (curationId: string): string => `my-stream-wave-tabpanel-curation-${curationId}`;
const getCurationTabKey = (curationId: string): string => `curation:${curationId}`;
const getCurationIdFromTabKey = (key: string): string => key.replace("curation:", "");
const getProfileCurationTooltipId = (curationId: string): string => `my-stream-profile-curation-${curationId}`;
const getEffectiveProfileCurationId = ({
  curations,
  isProfileWave,
  profileCurationId
}: {
  readonly curations: readonly {
    id: string;
  }[];
  readonly isProfileWave: boolean;
  readonly profileCurationId: string | null | undefined;
}): string | null => {
  if (!isProfileWave) {
    return null;
  }
  if (profileCurationId && curations.some(curation => curation.id === profileCurationId)) {
    return profileCurationId;
  }
  return curations[0]?.id ?? null;
};
const AUTO_EXPAND_LIMIT = 5;
const TAB_LABELS: Record<MyStreamWaveTab, string> = {
  [MyStreamWaveTab.CHAT]: "Chat",
  [MyStreamWaveTab.LEADERBOARD]: "Leaderboard",
  [MyStreamWaveTab.SUBMISSIONS]: "Submissions",
  [MyStreamWaveTab.SALES]: "Sales",
  [MyStreamWaveTab.WINNERS]: "Winners",
  [MyStreamWaveTab.OUTCOME]: "Outcome",
  [MyStreamWaveTab.MY_VOTES]: "My Votes",
  [MyStreamWaveTab.FAQ]: "FAQ"
};
const getTabLabel = ({
  isApproveWave,
  tab
}: {
  readonly isApproveWave: boolean;
  readonly tab: MyStreamWaveTab;
}): string => {
  if (isApproveWave && tab === MyStreamWaveTab.LEADERBOARD) {
    return "Approvals";
  }
  if (isApproveWave && tab === MyStreamWaveTab.WINNERS) {
    return "Approved";
  }
  return TAB_LABELS[tab];
};
const getWaveVotingState = ({
  isUpcoming,
  isCompleted
}: {
  readonly isUpcoming: boolean;
  readonly isCompleted: boolean;
}): WaveVotingState => {
  if (isUpcoming) {
    return WaveVotingState.NOT_STARTED;
  }
  if (isCompleted) {
    return WaveVotingState.ENDED;
  }
  return WaveVotingState.ONGOING;
};
interface DesktopTabButtonProps {
  readonly option: TabOption;
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
}
function DesktopTabButton(t0) {
  const $ = _c(17);
  const {
    option,
    activeKey,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== onSelect || $[1] !== option.key) {
    t1 = () => onSelect(option.key);
    $[0] = onSelect;
    $[1] = option.key;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = activeKey === option.key;
  const t3 = `tw-relative tw-whitespace-nowrap tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-bg-transparent tw-py-3 tw-text-sm tw-font-medium tw-transition-all tw-duration-200 ${activeKey === option.key ? "tw-border-primary-300 tw-text-white" : "tw-border-transparent tw-text-iron-500 desktop-hover:hover:tw-text-iron-200"}`;
  let t4;
  if ($[3] !== option.label) {
    t4 = <span className="tw-leading-5">{option.label}</span>;
    $[3] = option.label;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== option.leadingIcon || $[6] !== t4) {
    t5 = <span className="tw-inline-flex tw-h-5 tw-items-center tw-gap-1 tw-align-middle tw-leading-5">{t4}{option.leadingIcon}</span>;
    $[5] = option.leadingIcon;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== option.hasIndicator) {
    t6 = option.hasIndicator && <div className="tw-absolute -tw-right-1 tw-top-1 tw-h-2 tw-w-2 tw-rounded-full tw-bg-red" />;
    $[8] = option.hasIndicator;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== option.panelId || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t5 || $[15] !== t6) {
    t7 = <button onClick={t1} role="tab" aria-selected={t2} aria-controls={option.panelId} className={t3}>{t5}{t6}</button>;
    $[10] = option.panelId;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function ProfileCurationIcon(t0) {
  const $ = _c(3);
  const {
    tooltipId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <UserCircleIcon aria-hidden="true" className="tw-block tw-size-3.5 tw-flex-shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== tooltipId) {
    t2 = <span aria-label="Profile curation" data-tooltip-id={tooltipId} data-tooltip-content="Profile curation" className="tw-inline-flex tw-size-3.5 tw-flex-shrink-0 tw-items-center tw-justify-center tw-leading-none tw-text-primary-300">{t1}</span>;
    $[1] = tooltipId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function ReorderHandleIcon(t0) {
  const $ = _c(8);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="9" cy="6" r="1.5" />;
    t2 = <circle cx="15" cy="6" r="1.5" />;
    t3 = <circle cx="9" cy="12" r="1.5" />;
    t4 = <circle cx="15" cy="12" r="1.5" />;
    t5 = <circle cx="9" cy="18" r="1.5" />;
    t6 = <circle cx="15" cy="18" r="1.5" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== className) {
    t7 = <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>{t1}{t2}{t3}{t4}{t5}{t6}</svg>;
    $[6] = className;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}
function DesktopTabOption(t0) {
  const $ = _c(12);
  const {
    option,
    activeKey,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== activeKey || $[1] !== onSelect || $[2] !== option) {
    t1 = <DesktopTabButton option={option} activeKey={activeKey} onSelect={onSelect} />;
    $[0] = activeKey;
    $[1] = onSelect;
    $[2] = option;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== option.leadingIconTooltipId) {
    t2 = option.leadingIconTooltipId !== undefined && <MyStreamActionTooltip id={option.leadingIconTooltipId} />;
    $[4] = option.leadingIconTooltipId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== option.action) {
    t3 = option.action !== undefined && option.action !== null && <div className="tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-border-transparent">{option.action}</div>;
    $[6] = option.action;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="tw-flex tw-items-center">{t1}{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function SortableCurationTabOption(t0) {
  const $ = _c(36);
  const {
    option,
    activeKey,
    isSortingDisabled,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== isSortingDisabled || $[1] !== option.key) {
    t1 = {
      id: option.key,
      disabled: isSortingDisabled
    };
    $[0] = isSortingDisabled;
    $[1] = option.key;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition
  } = useSortable(t1);
  let t2;
  if ($[3] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[3] = transform;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isDragging ? 10 : undefined;
  const t4 = isDragging ? 0.85 : undefined;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4 || $[8] !== transition) {
    t5 = {
      transform: t2,
      transition,
      zIndex: t3,
      opacity: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = transition;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const style = t5;
  const reorderTooltipId = `${option.key}-reorder-tooltip`;
  const t6 = `Drag ${option.label} curation tab`;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ReorderHandleIcon className="tw-block tw-size-4 tw-flex-shrink-0" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== attributes || $[12] !== isSortingDisabled || $[13] !== listeners || $[14] !== reorderTooltipId || $[15] !== setActivatorNodeRef || $[16] !== t6) {
    t8 = <button ref={setActivatorNodeRef} type="button" disabled={isSortingDisabled} aria-label={t6} data-tooltip-id={reorderTooltipId} data-tooltip-content="Drag to reorder" className="tw-inline-flex tw-h-8 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-text-iron-600 tw-transition hover:tw-text-iron-300 disabled:tw-cursor-not-allowed disabled:tw-opacity-40" {...attributes} {...listeners}>{t7}</button>;
    $[11] = attributes;
    $[12] = isSortingDisabled;
    $[13] = listeners;
    $[14] = reorderTooltipId;
    $[15] = setActivatorNodeRef;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== reorderTooltipId) {
    t9 = <MyStreamActionTooltip id={reorderTooltipId} />;
    $[18] = reorderTooltipId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== activeKey || $[21] !== onSelect || $[22] !== option) {
    t10 = <DesktopTabButton option={option} activeKey={activeKey} onSelect={onSelect} />;
    $[20] = activeKey;
    $[21] = onSelect;
    $[22] = option;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== option.leadingIconTooltipId) {
    t11 = option.leadingIconTooltipId !== undefined && <MyStreamActionTooltip id={option.leadingIconTooltipId} />;
    $[24] = option.leadingIconTooltipId;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== option.action) {
    t12 = option.action !== undefined && option.action !== null && <div className="tw-border-x-0 tw-border-b-2 tw-border-t-0 tw-border-solid tw-border-transparent">{option.action}</div>;
    $[26] = option.action;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== setNodeRef || $[29] !== style || $[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t8 || $[34] !== t9) {
    t13 = <div ref={setNodeRef} style={style} className="tw-flex tw-items-center">{t8}{t9}{t10}{t11}{t12}</div>;
    $[28] = setNodeRef;
    $[29] = style;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t8;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
const MyStreamWaveDesktopTabs: React.FC<MyStreamWaveDesktopTabsProps> = t0 => {
  const $ = _c(141);
  const {
    activeTab,
    wave,
    setActiveTab,
    activeCurationId,
    onSelectCuration,
    showCreateCurationAction: t1
  } = t0;
  const showCreateCurationAction = t1 === undefined ? true : t1;
  const searchParams = useSearchParams();
  const {
    availableTabs,
    updateAvailableTabs,
    setActiveContentTab
  } = useContentTab();
  const {
    activeProfileProxy,
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const {
    isChatWave,
    isApproveWave,
    isMemesWave,
    isCurationWave,
    isRankWave,
    pauses: t2
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t2;
  const isCompetitionWave = isRankWave || isApproveWave;
  const {
    voting: t3,
    decisions: t4
  } = useWaveTimers(wave);
  const {
    isUpcoming,
    isCompleted
  } = t3;
  const {
    firstDecisionDone
  } = t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      initialPastWindow: 3,
      initialFutureWindow: 10
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const {
    allDecisions,
    hasMoreFuture,
    loadMoreFuture
  } = useDecisionPoints(wave, t5);
  let t6;
  if ($[1] !== wave.id) {
    t6 = {
      waveId: wave.id
    };
    $[1] = wave.id;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    data: t7
  } = useWaveCurations(t6);
  const curations = t7 === undefined ? [] : t7;
  const isConnectedProfileWaveAuthor = connectedProfile?.id === wave.author.id;
  const t8 = isConnectedProfileWaveAuthor ? connectedProfile : wave.author;
  let t9;
  if ($[3] !== t8) {
    t9 = getProfileWaveIdentity(t8);
    $[3] = t8;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const profileWaveIdentity = t9;
  const t10 = profileWaveIdentity.length > 0 && curations.length > 0;
  let t11;
  if ($[5] !== profileWaveIdentity || $[6] !== t10) {
    t11 = {
      identity: profileWaveIdentity,
      enabled: t10
    };
    $[5] = profileWaveIdentity;
    $[6] = t10;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const {
    data: profileWave
  } = useProfileWave(t11);
  let t12;
  if ($[8] !== wave.id) {
    t12 = {
      waveId: wave.id
    };
    $[8] = wave.id;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  const {
    reorderCuration,
    isPending: isCurationReorderPending
  } = useWaveCurationReorderMutation(t12);
  const canManageCurations = wave.wave.authenticated_user_eligible_for_admin === true;
  const isProfileWave = profileWave?.profile_wave_id === wave.id;
  const profileCurationId = getEffectiveProfileCurationId({
    curations,
    isProfileWave,
    profileCurationId: profileWave?.profile_curation_id
  });
  const canSetProfileCuration = isProfileWave && isConnectedProfileWaveAuthor && activeProfileProxy === null;
  let t13;
  if ($[10] !== allDecisions || $[11] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t13 = allDecisions.filter(decision_0 => filtered.some(item => item.decision_time === decision_0.timestamp));
    $[10] = allDecisions;
    $[11] = filterDecisionsDuringPauses;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  const filteredDecisions = t13;
  let t14;
  if ($[13] !== filteredDecisions) {
    t14 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[13] = filteredDecisions;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const nextDecisionTime = t14;
  const autoExpandFutureAttemptsRef = useRef(0);
  const desktopTabsScrollerRef = useRef(null);
  const mobileTabsScrollerRef = useRef(null);
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      activationConstraint: {
        distance: 6
      }
    };
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  const sortableSensors = useSensors(useSensor(PointerSensor, t15), useSensor(KeyboardSensor, t16));
  let t17;
  let t18;
  if ($[17] !== hasMoreFuture || $[18] !== loadMoreFuture || $[19] !== nextDecisionTime) {
    t17 = () => {
      const hasUpcoming = typeof nextDecisionTime === "number";
      if (hasUpcoming || !hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
      }, 50);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t18 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[17] = hasMoreFuture;
    $[18] = loadMoreFuture;
    $[19] = nextDecisionTime;
    $[20] = t17;
    $[21] = t18;
  } else {
    t17 = $[20];
    t18 = $[21];
  }
  useEffect(t17, t18);
  let t19;
  if ($[22] !== isCompleted || $[23] !== isUpcoming) {
    t19 = getWaveVotingState({
      isUpcoming,
      isCompleted
    });
    $[22] = isCompleted;
    $[23] = isUpcoming;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  const votingState = t19;
  let t20;
  if ($[25] !== firstDecisionDone || $[26] !== hasAuthenticatedProfile || $[27] !== isApproveWave || $[28] !== isChatWave || $[29] !== isCurationWave || $[30] !== isMemesWave || $[31] !== searchParams || $[32] !== updateAvailableTabs || $[33] !== votingState || $[34] !== wave.id) {
    t20 = () => {
      const hasSerialTarget = searchParams.get("serialNo") !== null;
      updateAvailableTabs({
        waveId: wave.id,
        isMemesWave,
        isChatWave,
        hasAuthenticatedProfile,
        isCurationWave,
        isApproveWave,
        votingState,
        hasFirstDecisionPassed: firstDecisionDone,
        transientPreferredTab: hasSerialTarget ? MyStreamWaveTab.CHAT : null
      });
    };
    $[25] = firstDecisionDone;
    $[26] = hasAuthenticatedProfile;
    $[27] = isApproveWave;
    $[28] = isChatWave;
    $[29] = isCurationWave;
    $[30] = isMemesWave;
    $[31] = searchParams;
    $[32] = updateAvailableTabs;
    $[33] = votingState;
    $[34] = wave.id;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== firstDecisionDone || $[37] !== hasAuthenticatedProfile || $[38] !== isApproveWave || $[39] !== isChatWave || $[40] !== isCurationWave || $[41] !== isMemesWave || $[42] !== searchParams || $[43] !== updateAvailableTabs || $[44] !== votingState || $[45] !== wave) {
    t21 = [wave, isMemesWave, isChatWave, isApproveWave, hasAuthenticatedProfile, isCurationWave, votingState, firstDecisionDone, searchParams, updateAvailableTabs];
    $[36] = firstDecisionDone;
    $[37] = hasAuthenticatedProfile;
    $[38] = isApproveWave;
    $[39] = isChatWave;
    $[40] = isCurationWave;
    $[41] = isMemesWave;
    $[42] = searchParams;
    $[43] = updateAvailableTabs;
    $[44] = votingState;
    $[45] = wave;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[47] !== activeCurationId || $[48] !== setActiveContentTab || $[49] !== wave.wave.type) {
    t22 = () => {
      if (wave.wave.type === ApiWaveType.Chat && !activeCurationId) {
        setActiveContentTab(MyStreamWaveTab.CHAT);
      }
    };
    t23 = [wave.wave.type, activeCurationId, setActiveContentTab];
    $[47] = activeCurationId;
    $[48] = setActiveContentTab;
    $[49] = wave.wave.type;
    $[50] = t22;
    $[51] = t23;
  } else {
    t22 = $[50];
    t23 = $[51];
  }
  useEffect(t22, t23);
  let t24;
  if ($[52] !== availableTabs || $[53] !== hasAuthenticatedProfile || $[54] !== isApproveWave || $[55] !== isCompetitionWave || $[56] !== isCurationWave || $[57] !== isMemesWave) {
    let t25;
    if ($[59] !== hasAuthenticatedProfile || $[60] !== isCompetitionWave || $[61] !== isCurationWave || $[62] !== isMemesWave) {
      t25 = tab => {
        if (tab === MyStreamWaveTab.MY_VOTES) {
          return isCurationWave || hasAuthenticatedProfile && (isMemesWave || isCompetitionWave);
        }
        if (tab === MyStreamWaveTab.SALES) {
          return isCurationWave;
        }
        if (tab === MyStreamWaveTab.FAQ) {
          return isMemesWave;
        }
        return true;
      };
      $[59] = hasAuthenticatedProfile;
      $[60] = isCompetitionWave;
      $[61] = isCurationWave;
      $[62] = isMemesWave;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    let t26;
    if ($[64] !== isApproveWave) {
      t26 = tab_0 => ({
        key: tab_0,
        label: getTabLabel({
          isApproveWave,
          tab: tab_0
        }),
        panelId: getContentTabPanelId(tab_0)
      });
      $[64] = isApproveWave;
      $[65] = t26;
    } else {
      t26 = $[65];
    }
    t24 = availableTabs.filter(t25).map(t26);
    $[52] = availableTabs;
    $[53] = hasAuthenticatedProfile;
    $[54] = isApproveWave;
    $[55] = isCompetitionWave;
    $[56] = isCurationWave;
    $[57] = isMemesWave;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  const standardOptions = t24;
  let t25;
  if ($[66] !== activeCurationId || $[67] !== canManageCurations || $[68] !== canSetProfileCuration || $[69] !== curations || $[70] !== onSelectCuration || $[71] !== profileCurationId || $[72] !== wave) {
    let t26;
    if ($[74] !== activeCurationId || $[75] !== canManageCurations || $[76] !== canSetProfileCuration || $[77] !== onSelectCuration || $[78] !== profileCurationId || $[79] !== wave) {
      t26 = curation => ({
        key: getCurationTabKey(curation.id),
        label: curation.name,
        panelId: getCurationPanelId(curation.id),
        leadingIcon: curation.id === profileCurationId ? <ProfileCurationIcon tooltipId={getProfileCurationTooltipId(curation.id)} /> : undefined,
        leadingIconTooltipId: curation.id === profileCurationId ? getProfileCurationTooltipId(curation.id) : undefined,
        action: canManageCurations ? <MyStreamWaveCurationTabMenu wave={wave} curation={curation} canSetAsProfileCuration={canSetProfileCuration && curation.id !== profileCurationId} onDeleted={activeCurationId === curation.id ? () => onSelectCuration(null) : undefined} /> : undefined
      });
      $[74] = activeCurationId;
      $[75] = canManageCurations;
      $[76] = canSetProfileCuration;
      $[77] = onSelectCuration;
      $[78] = profileCurationId;
      $[79] = wave;
      $[80] = t26;
    } else {
      t26 = $[80];
    }
    t25 = curations.map(t26);
    $[66] = activeCurationId;
    $[67] = canManageCurations;
    $[68] = canSetProfileCuration;
    $[69] = curations;
    $[70] = onSelectCuration;
    $[71] = profileCurationId;
    $[72] = wave;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  const curationOptions = t25;
  let t26;
  if ($[81] !== curationOptions || $[82] !== standardOptions) {
    t26 = [...standardOptions, ...curationOptions];
    $[81] = curationOptions;
    $[82] = standardOptions;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  const options = t26;
  let t27;
  if ($[84] !== activeCurationId || $[85] !== activeTab) {
    t27 = activeCurationId ? getCurationTabKey(activeCurationId) : activeTab;
    $[84] = activeCurationId;
    $[85] = activeTab;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  const activeKey = t27;
  let t28;
  if ($[87] !== curations) {
    t28 = curations.map(_temp3);
    $[87] = curations;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  const curationTabKeys = t28;
  const canDragCurations = canManageCurations && curations.length > 1;
  let t29;
  if ($[89] !== curations || $[90] !== reorderCuration) {
    t29 = t30 => {
      const {
        active,
        over
      } = t30;
      if (over === null || active.id === over.id) {
        return;
      }
      const curationId = getCurationIdFromTabKey(String(active.id));
      const targetIndex = curations.findIndex(curation_1 => getCurationTabKey(curation_1.id) === over.id);
      const curation_2 = curations.find(item_0 => item_0.id === curationId) ?? null;
      if (curation_2 === null || targetIndex < 0) {
        return;
      }
      reorderCuration({
        curation: curation_2,
        targetPriorityOrder: targetIndex + 1,
        curations
      });
    };
    $[89] = curations;
    $[90] = reorderCuration;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  const handleCurationDragEnd = t29;
  let t30;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => {
      const frameId = globalThis.window.requestAnimationFrame(() => {
        [desktopTabsScrollerRef.current, mobileTabsScrollerRef.current].forEach(_temp4);
      });
      return () => {
        globalThis.window.cancelAnimationFrame(frameId);
      };
    };
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  let t31;
  if ($[93] !== activeKey || $[94] !== options) {
    t31 = [activeKey, options];
    $[93] = activeKey;
    $[94] = options;
    $[95] = t31;
  } else {
    t31 = $[95];
  }
  useEffect(t30, t31);
  if (isChatWave && !canManageCurations && curations.length === 0) {
    return null;
  }
  let t32;
  if ($[96] !== onSelectCuration || $[97] !== setActiveTab) {
    t32 = key => {
      if (key.startsWith("curation:")) {
        onSelectCuration(getCurationIdFromTabKey(key));
        return;
      }
      onSelectCuration(null);
      setActiveTab(key as MyStreamWaveTab);
    };
    $[96] = onSelectCuration;
    $[97] = setActiveTab;
    $[98] = t32;
  } else {
    t32 = $[98];
  }
  let t33;
  if ($[99] !== activeKey || $[100] !== options || $[101] !== t32) {
    t33 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 sm:tw-hidden"><div ref={mobileTabsScrollerRef} className="tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-inline-flex tw-items-center tw-gap-1"><TabToggle options={options} activeKey={activeKey} onSelect={t32} /></div></div></div>;
    $[99] = activeKey;
    $[100] = options;
    $[101] = t32;
    $[102] = t33;
  } else {
    t33 = $[102];
  }
  let t34;
  if ($[103] !== activeKey || $[104] !== onSelectCuration || $[105] !== setActiveTab || $[106] !== standardOptions) {
    let t35;
    if ($[108] !== activeKey || $[109] !== onSelectCuration || $[110] !== setActiveTab) {
      t35 = option => <DesktopTabOption key={option.key} option={option} activeKey={activeKey} onSelect={key_0 => {
        onSelectCuration(null);
        setActiveTab(key_0 as MyStreamWaveTab);
      }} />;
      $[108] = activeKey;
      $[109] = onSelectCuration;
      $[110] = setActiveTab;
      $[111] = t35;
    } else {
      t35 = $[111];
    }
    t34 = standardOptions.map(t35);
    $[103] = activeKey;
    $[104] = onSelectCuration;
    $[105] = setActiveTab;
    $[106] = standardOptions;
    $[107] = t34;
  } else {
    t34 = $[107];
  }
  let t35;
  if ($[112] !== activeKey || $[113] !== canDragCurations || $[114] !== curationOptions || $[115] !== isCurationReorderPending || $[116] !== onSelectCuration) {
    let t36;
    if ($[118] !== activeKey || $[119] !== canDragCurations || $[120] !== isCurationReorderPending || $[121] !== onSelectCuration) {
      t36 = option_0 => <React.Fragment key={option_0.key}>{canDragCurations ? <SortableCurationTabOption option={option_0} activeKey={activeKey} isSortingDisabled={isCurationReorderPending} onSelect={key_1 => onSelectCuration(getCurationIdFromTabKey(key_1))} /> : <DesktopTabOption option={option_0} activeKey={activeKey} onSelect={key_2 => onSelectCuration(getCurationIdFromTabKey(key_2))} />}</React.Fragment>;
      $[118] = activeKey;
      $[119] = canDragCurations;
      $[120] = isCurationReorderPending;
      $[121] = onSelectCuration;
      $[122] = t36;
    } else {
      t36 = $[122];
    }
    t35 = curationOptions.map(t36);
    $[112] = activeKey;
    $[113] = canDragCurations;
    $[114] = curationOptions;
    $[115] = isCurationReorderPending;
    $[116] = onSelectCuration;
    $[117] = t35;
  } else {
    t35 = $[117];
  }
  let t36;
  if ($[123] !== curationTabKeys || $[124] !== t35) {
    t36 = <SortableContext items={curationTabKeys} strategy={horizontalListSortingStrategy}>{t35}</SortableContext>;
    $[123] = curationTabKeys;
    $[124] = t35;
    $[125] = t36;
  } else {
    t36 = $[125];
  }
  let t37;
  if ($[126] !== handleCurationDragEnd || $[127] !== sortableSensors || $[128] !== t36) {
    t37 = <DndContext sensors={sortableSensors} collisionDetection={closestCenter} onDragEnd={handleCurationDragEnd}>{t36}</DndContext>;
    $[126] = handleCurationDragEnd;
    $[127] = sortableSensors;
    $[128] = t36;
    $[129] = t37;
  } else {
    t37 = $[129];
  }
  let t38;
  if ($[130] !== t34 || $[131] !== t37) {
    t38 = <div ref={desktopTabsScrollerRef} className="tw-hidden tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-block"><div className="tw-flex tw-w-auto tw-gap-x-1" role="tablist">{t34}{t37}</div></div>;
    $[130] = t34;
    $[131] = t37;
    $[132] = t38;
  } else {
    t38 = $[132];
  }
  let t39;
  if ($[133] !== onSelectCuration || $[134] !== showCreateCurationAction || $[135] !== wave) {
    t39 = showCreateCurationAction && <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 sm:tw-ml-auto"><MyStreamWaveCreateCurationAction wave={wave} onCreated={onSelectCuration} /></div>;
    $[133] = onSelectCuration;
    $[134] = showCreateCurationAction;
    $[135] = wave;
    $[136] = t39;
  } else {
    t39 = $[136];
  }
  let t40;
  if ($[137] !== t33 || $[138] !== t38 || $[139] !== t39) {
    t40 = <div className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-2 tw-@container/tabs sm:tw-px-4">{t33}{t38}{t39}</div>;
    $[137] = t33;
    $[138] = t38;
    $[139] = t39;
    $[140] = t40;
  } else {
    t40 = $[140];
  }
  return t40;
};
export default MyStreamWaveDesktopTabs;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(curation_0) {
  return getCurationTabKey(curation_0.id);
}
function _temp4(scroller) {
  if (!scroller) {
    return;
  }
  const activeTabElement = scroller.querySelector("[role=\"tab\"][aria-selected=\"true\"]");
  activeTabElement?.scrollIntoView({
    block: "nearest",
    inline: "nearest"
  });
}